import { useCallback, useEffect, useRef, useState } from 'react';
import uuid from 'uuid/v4';

import { type DocNode } from '@atlaskit/adf-schema';

import { useStagingAreaActions } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import {
	CHAT_STORE_STATUS,
	isStoreError,
	isStoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import type { StoreChatItem } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useSmartCreationChatStoreApi } from '@atlassian/conversation-assistant/controllers/smart-creation-api';
import { findLastAssistantAction } from '@atlassian/conversation-assistant/controllers/chat-store/utils';
import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	getSubTypeDisplayName,
	type RemixOptionSelectionParameters,
} from '@atlassian/malleable-ui-components';
import {
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	isRemixGeneratedFlow,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { usePageContentId, useContentType, usePageSpaceKey } from '@confluence/page-context';
import { expValEquals } from '@confluence/feature-experiments';

import type { DynamicUiSource } from './common-types.ts';

const isActionComplete = (chatMessages: StoreChatItem[]): boolean => {
	// Scan backwards to find ACTION_COMPLETE in the latest turn.
	// If we encounter a HUMAN message first, that marks a turn boundary —
	// any ACTION_COMPLETE before it belongs to a previous turn.
	for (let i = chatMessages.length - 1; i >= 0; i--) {
		const message = chatMessages[i];

		if (message.type === 'ACTION_COMPLETE') {
			return true;
		}

		if (isStoreHuman(message)) {
			return false;
		}
	}

	return false;
};

function shouldOpenEphemeralPreview(chatMessages: StoreChatItem[]): boolean {
	for (let i = chatMessages.length - 1; i >= 0; i--) {
		const message = chatMessages[i];
		if (message.type === 'ACTION_PREPARE') {
			return message.action ? isRemixGeneratedFlow(message.action) : false;
		}
	}
	return false;
}

/**
 * Checks whether any assistant message contains a whiteboard create or update
 * action. This is used to detect the ACTION_PREPARE phase for whiteboard remix
 * actions, since the assistant message is updated with action data during
 * streaming (before ACTION_COMPLETE).
 */
const hasWhiteboardRemixAction = (chatMessages: StoreChatItem[]): boolean =>
	findLastAssistantAction(
		chatMessages,
		(action) =>
			action.key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY ||
			action.key === CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	) !== undefined;

/**
 * Hook that provides a callback to send selected text to the AIFC staging modal
 * as a Rovo chat message, skipping the prompt screen and going straight to the
 * staging area preview.
 *
 * This is used by the Remix feature to hand off option selection
 * to the AIFC Create with Rovo flow.
 *
 * @param onActionReady - Optional callback invoked when the action is ready for
 *   the consumer to react (e.g. open the staging modal). For most actions this
 *   fires on ACTION_COMPLETE; for whiteboard create/update actions it fires
 *   earlier, when ACTION_PART data first appears in the assistant message.
 */
export const useRemixToAIFC = ({
	onActionReady,
}: {
	onActionReady?: () => void;
} = {}) => {
	const { sendMessage, createConversationOptimistically, abortRequests, activeConversation } =
		useSmartCreationChatStoreApi();
	const { setPrompt, setDynamicUiContext, setForceSkipAutoConversationSelection } =
		useStagingAreaActions();
	const { preferences } = useUserPreferences();
	const { openModal, closeModal } = useSmartCreationModalActions();
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const isLivePage = useIsCurrentPageLive();

	// Track the conversation ID created by this remix flow. Only when the
	// store's activeConversation matches this ID do we consider it "ours".
	// Stored as state so that setting it triggers a rerender, ensuring
	// derived values (activeRemixConversation, chatMessages, etc.) update
	// immediately rather than waiting for an external rerender.
	const [remixConversationId, setRemixConversationId] = useState<string | undefined>(undefined);

	const activeRemixConversation =
		activeConversation?.id && activeConversation.id === remixConversationId
			? activeConversation
			: null;

	const [chatMessages] = useChatMessages({ conversationId: activeRemixConversation?.id ?? '' });

	// Detect whether Rovo encountered an error while processing a remix request.
	// This covers ACTION_ERROR at any stage — both before the ephemeral preview
	// opens (during awaitingResponse) and while it is loading (during typingResponse).
	// Uses the same isStoreError check as the smart-creation staging area.
	const isRemixError =
		activeRemixConversation?.status === CHAT_STORE_STATUS.deactivatedAgentError ||
		chatMessages.some((message) => isStoreError(message));

	// Track whether the onActionReady callback has already been fired for the
	// current conversation to avoid duplicate invocations.
	const actionReadyFired = useRef(false);

	// Reset the fired flag when the active remix conversation changes (new remix flow).
	const prevConversationId = useRef(activeRemixConversation?.id);
	useEffect(() => {
		if (prevConversationId.current !== activeRemixConversation?.id) {
			actionReadyFired.current = false;
			prevConversationId.current = activeRemixConversation?.id;
		}
	}, [activeRemixConversation?.id]);

	// Fire onActionReady when the appropriate message arrives:
	// - For whiteboard create/update actions: when ACTION_PART data appears in the
	//   assistant message (detected via hasWhiteboardRemixAction).
	// - For all other actions: when ACTION_COMPLETE appears in chatMessages.
	useEffect(() => {
		if (actionReadyFired.current || !onActionReady) {
			return;
		}

		const whiteboardDetected = hasWhiteboardRemixAction(chatMessages);
		const actionCompleteDetected = isActionComplete(chatMessages);

		if (whiteboardDetected || actionCompleteDetected) {
			actionReadyFired.current = true;
			onActionReady();
		}
	}, [chatMessages, onActionReady]);

	const stopRemixing = useCallback(() => {
		abortRequests?.({ conversationId: activeRemixConversation?.id });
	}, [abortRequests, activeRemixConversation?.id]);

	const openAIFCStagingModal = useCallback(() => {
		setForceSkipAutoConversationSelection(true);
		// Open the Confluence Smart Creation Modal with stagingAreaActive=true
		// to skip the prompt screen and go straight to the staging area preview.
		// A no-op submitCallback is passed so that handleSubmit in ConfluenceSmartCreationModal
		// invokes this callback instead of navigating away from the current page.
		//
		// Only keep the ephemeral preview for remix-generated preview content.
		// The shared helper currently treats dynamic UI, image-based remix flows,
		// and remix-generated whiteboards/diagrams as ephemeral-preview candidates.
		// All other ACTION_PREPARE results should open the full AIFC modal.
		const shouldUseEphemeralPreview = shouldOpenEphemeralPreview(chatMessages);

		openModal({
			entryPoint: 'dynamicUi',
			spaceKey: spaceKey ?? undefined,
			stagingAreaActive: true,
			isFloating: shouldUseEphemeralPreview,
			shouldShowBackButton: false,
			...(activeRemixConversation?.id && expValEquals('cc-maui-experiment', 'isEnabled', true)
				? { remixConversationId: activeRemixConversation.id }
				: {}),
			content: contentId
				? {
						id: contentId,
						type: contentType ?? 'page',
						subType: isLivePage ? 'live' : null,
					}
				: undefined,
			submitCallback: async () => {},
		});
	}, [
		openModal,
		spaceKey,
		contentId,
		contentType,
		isLivePage,
		activeRemixConversation?.id,
		setForceSkipAutoConversationSelection,
		chatMessages,
	]);

	const sendAIFCPrompt = useCallback(
		async ({
			userPrompt,
			dynamicUiSubtype,
			dynamicUiType,
			dynamicUiSource,
			interactionSource,
		}: {
			userPrompt?: string;
			dynamicUiType: RemixOptionSelectionParameters['type'];
			dynamicUiSubtype?: string;
			dynamicUiSource: DynamicUiSource | undefined;
			interactionSource?: InteractionSource;
		}) => {
			// Build the ADF DocNode prompt.
			const prompt: DocNode = {
				type: 'doc',
				version: 1,
				content: [
					{
						type: 'paragraph',
						content: [
							{
								type: 'text',
								text:
									dynamicUiType === 'custom'
										? (userPrompt ?? 'Remix the selected content')
										: dynamicUiSubtype
											? `Remix the selected content as a ${getSubTypeDisplayName(dynamicUiSubtype)}`
											: 'Remix the selected content',
							},
						],
					},
				],
			};

			const newConversationId = uuid();
			// Store the ID so we can identify this conversation as the active remix.
			setRemixConversationId(newConversationId);
			actionReadyFired.current = false;

			await createConversationOptimistically({ id: newConversationId });

			// Send the message to Rovo
			sendMessage({
				prompt,
				storeUserMessages: preferences.storeUserMessages,
				conversationId: newConversationId,
				messageSources: {
					interactionSource,
					// Use the same source for follow-ups: Remix doesn't have a separate follow-up entry point, but the "viewed" AIMAU analytics event uses `followUpSource`.
					followUpSource: interactionSource,
				},
				productKey: 'confluence',
				contentContext: 'staging-area',
				creationContextParams: {
					dynamicUiType,
					dynamicUiSubtype,
					dynamicUiSource,
					source: 'remix',
				},
			});

			// Store the prompt text for display in the staging area header.
			// Always show "Remix" regardless of the user's custom prompt.
			setPrompt('Remix');

			// Persist the dynamic UI context so the update flow can use
			// the original type/source when sending remix adjustment messages
			setDynamicUiContext(dynamicUiType, dynamicUiSource);
		},
		[
			createConversationOptimistically,
			sendMessage,
			setPrompt,
			setDynamicUiContext,
			preferences.storeUserMessages,
		],
	);

	return {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		openAIFCStagingModal,
		closeAIFCStagingModal: closeModal,
	};
};
